import {
  BrandType,
  Button,
  ButtonContainer,
  Col,
  Flex,
  H4,
  InlineAlert,
  Row,
} from '@appliedsystems/applied-design-system';
import {
  AdyenPaymentMethod,
  ErrorDetails,
  PaymentMethod,
  PfErrorCategory,
  TransactionType,
} from '@appliedsystems/payments-core';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { usePaymentMethodStore } from 'src/store/PaymentMethod';
import { AdyenCheckoutState } from 'src/util/adyen';
import { ApiClient } from '../../api/ApiClient';
import { currencyMap } from '../../constants/constants';
import { useAccountManagement } from '../../hooks/useAccountManagement';
import { usePaymentsTranslation } from '../../hooks/usePaymentsTranslation';
import { useAccountManagementStore } from '../../store/AccountManagement';
import { useAgencyDetailsStore } from '../../store/AgencyDetail';
import { Locale } from '../../store/Locale';
import { getAmountWithFees } from '../../util/getAmountWithFees';
import { AcceptCCFeeCheckbox } from '../AcceptCCFeeCheckbox/AcceptCCFeeCheckbox';
import { AdyenWebComponent } from '../AdyenWebComponent/AdyenWebComponent';
import { AmountBreakdown } from '../AmountBreakdown/AmountBreakdown';
import { Disclaimer } from '../Disclaimer/Disclaimer';
import { EftComponent } from '../EftComponent/EftComponent';
import { ErrorAlert, ErrorMessage } from '../ErrorAlert/ErrorAlert';
import { ExpressCheckout } from '../ExpressCheckout/ExpressCheckout';
import { FullInvoiceGroup } from '../HostedPaymentPageContainer/types';
import { useHppDataStore } from '../HostedPaymentPageContainer/useHppData';
import { useHppSessionStore } from '../HostedPaymentPageContainer/useHppSession';
import { PaymentMethodSelection } from '../PaymentMethodSelection/PaymentMethodSelection';
import { PaymentOptionContainer } from '../PaymentOptionContainer';
import { PaymentOptionDetails } from '../PaymentOptionDetails/PaymentOptionDetails';
import classes from './PaymentMethodContainer.module.scss';

type Props = {
  onPaymentSuccess: (state: AdyenCheckoutState) => void;
  onDataValidated?: () => void;
  onBack: () => void;
  isDownPayment: boolean;
};

export const PaymentMethodContainer = ({
  onPaymentSuccess,
  onDataValidated,
  onBack,
  isDownPayment,
}: Props): React.ReactElement => {
  const [isCCFeeAccepted, setIsCCFeeAccepted] = useState(false);
  const { data: agencyDetails } = useAgencyDetailsStore();
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();
  const [isPfaSigned, setIsPfaSigned] = useState(false);
  const { t } = usePaymentsTranslation();
  const { paymentMethodConfig, hppData, setHppData, selectedInvoices } = useHppDataStore();

  const handleCheckboxChange = (checked: boolean) => {
    setIsCCFeeAccepted(checked);
  };
  const { isCcFeeCheckboxEnabled, ccFeeThresholdAmount } = agencyDetails || {};
  const hppToken = agencyDetails?.token;
  const { isAccountManagementEnabled: isAccountManagementFlagEnabled, customerUser } = useAccountManagement(
    agencyDetails?.appliedProductId,
    hppToken,
  );
  const isAccountManagementEnabled = isAccountManagementFlagEnabled && agencyDetails?.manageMyAccountEnabled;
  const { loginModal, customerUser: user } = useAccountManagementStore();
  const { locale } = Locale.useContainer();
  const { hppSession } = useHppSessionStore();
  const { data: paymentMethods } = usePaymentMethodStore();

  const storedPaymentMethods = useQuery(['paymentMethods', customerUser?.id], async () => {
    if (!customerUser) return null;
    const response = await ApiClient.getInstance().getStoredPaymentMethods();
    if (response.status !== 'ok' || !response.data) {
      console.error(`Invalid response from server`, response);
      throw new Error(`Invalid response from server`);
    }

    return response.data;
  });

  // Determine if the fee checkbox should be shown. It is shown if:
  // - The selected payment method is a card
  // - The credit card fee checkbox feature is enabled
  // - The amount is greater than or equal to the credit card fee threshold amount (or 0 if the threshold is not defined)
  // - The credit fee is not 0

  const showFeeCheckbox =
    hppData.paymentMethod === PaymentMethod.Card &&
    isCcFeeCheckboxEnabled &&
    hppData.paymentAmount >= (ccFeeThresholdAmount ?? 0) &&
    paymentMethodConfig[PaymentMethod.Card].fee !== 0;

  // Determine if the "Pay Now" button should be disabled. It is disabled if:
  // - The fee checkbox is shown and the fee has not been accepted
  const disablePayNowButton = Boolean(showFeeCheckbox && !isCCFeeAccepted);

  useEffect(() => {
    // If we are in down payment accordion, we need to calculate the subtotal differently
    // isDownPayment here in the useEffect is because I want to rerun this useEffect when user navigates back to the payment method accordion from down payment accordion
    if (hppData.paymentOption === TransactionType.PREMIUM_FINANCE || isDownPayment) {
      if (!hppData.pf?.quote) {
        return;
      }

      const { amountFinanced, docStampFee } = hppData.pf.quote;
      const pfSubtotal = hppData.paymentAmount - amountFinanced + (docStampFee ?? 0);

      setHppData({
        pf: {
          ...hppData.pf,
          pfSubtotal,
        },
        paymentMethod: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hppData.paymentOption, isDownPayment]);

  useEffect(() => {
    // Reset the isPfaSigned and signeeInfo state when the user changes in the dependency array
    if (isPfaSigned) {
      setIsPfaSigned(false);
      setHppData({
        pf: {
          ...hppData.pf,
          signeeInfo: undefined,
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hppData.firstName,
    hppData.lastName,
    hppData.userEmail,
    hppData.businessName,
    hppData.postalCode,
    hppData.phoneNumber,
    hppData.invoiceNumber,
    hppData.accountCode,
    selectedInvoices,
  ]);

  /**
   * We should grey out financing as an option when financing could have been
   * available, but isn't due to missing PFC data for the given invoices.
   *
   * This can happen in any of the following cases:
   *
   * 1. While consolidating a quote (createQuote), the total consolidated
   *    premium amount exceeds the provider's maximum ($100k at the time of
   *    writing) and one or more of the policies being financed is missing PFC
   *    data.
   * 2. In HPP, one or more of the invoices selected is missing PFC data.
   * 3. In Generate Invoice, the user doesn't select invoices; so if no
   *    invoices were explicitly selected, but the quickQuote stage yields
   *    invoices that failed the PFC data check, we should also grey out
   *    financing.
   */
  const calculatePfcErrorCondition = useCallback(
    (
      failedPfcDataCheckInvoices: string[] | undefined,
      selectedInvoices: FullInvoiceGroup[],
      pfErrors: ErrorDetails[] | null | undefined,
    ) => {
      // Case 1
      const consolidationPfcErrors = !!pfErrors?.some(
        (error) => error.pfErrorCategory === PfErrorCategory.PFC_DATA_MISSING,
      );

      // Case 2
      const areSelectedInvoicesMissingPfcData = !!failedPfcDataCheckInvoices?.some((failedInvoiceNumber) =>
        selectedInvoices.map((selectedInvoice) => selectedInvoice.invoiceNumber).includes(failedInvoiceNumber),
      );

      // Case 3
      const quickQuotePfcErrors =
        selectedInvoices.length === 0 && failedPfcDataCheckInvoices && failedPfcDataCheckInvoices.length > 0;

      return consolidationPfcErrors || areSelectedInvoicesMissingPfcData || quickQuotePfcErrors;
    },
    [],
  );

  const weHaveAPfQuote = !!hppData.pf?.quote?.quoteId;
  // TODO: PAY-XXXX do state calculations in central store
  // TODO: PAY-XXXX

  const weHavePfcErrors = calculatePfcErrorCondition(
    hppData.pf?.quickQuote?.failedPfcDataCheckInvoices,
    selectedInvoices,
    hppData.pf?.errors,
  );

  const applePayConfig =
    paymentMethods &&
    paymentMethods.find((paymentMethod) => paymentMethod.type === AdyenPaymentMethod.ApplePay)?.configuration;

  const expressCheckoutEnabled =
    ((hppSession?.allowedPaymentMethods.includes(PaymentMethod.ApplePay) &&
      agencyDetails?.applePayEnabled &&
      applePayConfig) ||
      (hppSession?.allowedPaymentMethods.includes(PaymentMethod.GooglePay) && agencyDetails?.googlePayEnabled)) &&
    hppData.mobileWalletsEnabled; // This checks the FF and randomizer

  /**
   * If we're in a case where we should be greying out financing, but the user
   * has already selected financing in an earlier quote, we should reseet the
   * selected payment option to be PAID_IN_FULL.
   */
  useEffect(() => {
    if (!weHaveAPfQuote && weHavePfcErrors) {
      setHppData({ paymentOption: TransactionType.PAID_IN_FULL });
    }
  }, [weHaveAPfQuote, weHavePfcErrors, setHppData]);

  return (
    <Flex className={classes.paymentContainer}>
      {/* This component handles both "Pay in Full" and "Pay with Financing" options. It displays the financing card if there is a quote ID and the user is not in the down payment accordion. */}
      {((weHaveAPfQuote && !isDownPayment) || (!weHaveAPfQuote && weHavePfcErrors)) && (
        <PaymentOptionContainer
          pfDisabled={!weHaveAPfQuote && weHavePfcErrors}
          pfInfo={hppData.pf!}
          onPaymentOptionSelected={(paymentDetails) => {
            const paymentOption = paymentDetails.paymentOption!;
            setHppData({
              paymentOption: paymentOption,
              paymentMethod: undefined,
            });
          }}
        />
      )}

      {isDownPayment && <AmountBreakdown />}
      {/* if the flow is not premium finance or if it is in down payment accordion -  component is used here to allow the user to select a payment method */}
      {hppData.paymentOption === TransactionType.PAID_IN_FULL ||
      (!weHaveAPfQuote && weHavePfcErrors) ||
      isDownPayment ? (
        <>
          {expressCheckoutEnabled && !isDownPayment && (
            <ExpressCheckout
              onPaymentSuccess={onPaymentSuccess}
              setErrorMessage={setErrorMessage}
              isDownPayment={isDownPayment}
            />
          )}
          {(!weHaveAPfQuote || expressCheckoutEnabled) && <H4 className="mb-0">{t('CHOOSE_YOUR_PAYMENT_METHOD')}</H4>}
          {/* Show payment methods selection (radio buttons) */}
          {(hppData.paymentAmount || hppData.pf?.quote?.downPaymentAmount) && (
            <PaymentMethodSelection
              pfDisabled={weHavePfcErrors}
              isDownPayment={isDownPayment}
              onPaymentMethodSelected={(paymentDetails) => {
                let paymentFee: number;
                let paymentTotal: number;
                const paymentMethod = paymentDetails.paymentMethod!;
                const subtotal =
                  isDownPayment && hppData.pf?.quote
                    ? BigInt(hppData.pf.pfSubtotal!) + BigInt(hppData.pf.quote.retainedPayments ?? 0)
                    : hppData.paymentAmount;
                if (
                  paymentMethod === PaymentMethod.Card &&
                  typeof hppSession?.feeCredit === 'number' &&
                  hppData.paymentOption === TransactionType.PAID_IN_FULL
                ) {
                  paymentFee = hppSession.feeCredit;
                  paymentTotal = hppData.paymentAmount + paymentFee;
                } else {
                  const result = getAmountWithFees(
                    paymentMethod,
                    Number(subtotal),
                    paymentMethodConfig[paymentDetails.paymentMethod!].fee,
                    currencyMap[locale],
                    hppSession?.useVarAchFee,
                    hppSession?.useVarEftFee,
                  );
                  paymentFee = result.paymentFee;
                  paymentTotal = result.paymentTotal;
                }
                setHppData({
                  paymentMethod,
                  paymentTotal,
                  paymentFee,
                });
              }}
            />
          )}
          {/* use payment method on file button */}
          <div className={classes.buttonsContainer}>
            {isAccountManagementEnabled && !customerUser && (
              <Button className={classes.paymentMethodOnFileButton} onClick={!user ? loginModal.open : undefined}>
                {t('USE_PAYMENT_METHOD_ON_FILE')}
              </Button>
            )}
            {/* This back button is displayed in the UI before the user selects a payment method. */}
            {!hppData.paymentMethod && (
              <Button type="tertiary" onClick={onBack}>
                {t('BACK')}
              </Button>
            )}
          </div>
          {/* Adyen payment info form component */}
          {hppData.paymentMethod && hppData.paymentMethod !== PaymentMethod.Eft && (
            <Row>
              <Col xs={12}>
                <AdyenWebComponent
                  type={hppData.paymentMethod}
                  disablePayNowButton={disablePayNowButton}
                  onPaymentSuccess={onPaymentSuccess}
                  onBack={onBack}
                  setErrorMessage={setErrorMessage}
                  storedPaymentMethods={
                    storedPaymentMethods.data?.[
                      hppData.paymentMethod === PaymentMethod.Card ? 'cards' : hppData.paymentMethod
                    ]
                  }
                />
              </Col>
            </Row>
          )}
          {hppData.paymentMethod && hppData.paymentMethod === PaymentMethod.Eft && (
            <Row>
              <Col xs={12}>
                <EftComponent
                  onPaymentSuccess={onPaymentSuccess}
                  setErrorMessage={setErrorMessage}
                  onBack={onBack}
                  storedPaymentMethods={storedPaymentMethods.data?.[hppData.paymentMethod]}
                  hideSavePaymentMethod={!user}
                />
              </Col>
            </Row>
          )}
          {showFeeCheckbox && (
            <Row>
              <Col xs={12} className={classes.checkboxWrapper}>
                <AcceptCCFeeCheckbox onCheckboxChange={handleCheckboxChange} isChecked={isCCFeeAccepted} />
              </Col>
            </Row>
          )}
          {!!errorMessage && <ErrorAlert errorMessage={errorMessage} />}
          {!!storedPaymentMethods.isError && (
            <Flex className={classes.error}>
              <InlineAlert type={BrandType.Warning} closeable={true}>
                {t('ERROR_FAILED_TO_LOAD_PAYMENT_METHODS')}
              </InlineAlert>
            </Flex>
          )}
          <Disclaimer />
        </>
      ) : (
        <>
          {/* PF Info area, including signPfa stuff */}
          <PaymentOptionDetails pfInfo={hppData.pf!} isPfaSigned={isPfaSigned} setIsPfaSigned={setIsPfaSigned} />
          <ButtonContainer>
            <Button type="tertiary" onClick={onBack}>
              {t('BACK')}
            </Button>
            <Button type="primary" disabled={!isPfaSigned} onClick={onDataValidated}>
              {t('CONTINUE_TO_DOWN_PAYMENT')}
            </Button>
          </ButtonContainer>
        </>
      )}
    </Flex>
  );
};
